import { useMemo } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { ApiError } from '@/shared/api/rest/client'
import { ParentsService } from '@/shared/api/rest/services/parents.service'
import { useUserTypes } from '@/shared/hooks/useUserTypes'
import { UserTypes } from '@/shared/types/auth'
import { DuplicateParty, getPartyName } from '@/pages/user/teacher/duplicates/types'
import { useMyParentRecord } from './useParentDuplicates'

const PARTY_STALE_MS = 5 * 60_000

export type PartyMap = Record<string, DuplicateParty | undefined>

/**
 * Fetch a parent in full. A 404 is a real answer here — resolving a pair deletes
 * one of its two parents — so it resolves to `null` rather than erroring, while
 * anything else (401, 500, network) propagates so the UI can say "couldn't
 * load" instead of "no name".
 */
const fetchParty = async (id: string): Promise<DuplicateParty | null> => {
    try {
        return (await ParentsService.byId(id)) as DuplicateParty
    } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null
        throw error
    }
}

/**
 * Hydrate the two parents of a duplicate pair, in full: user details, branch,
 * and every child registered under each side.
 *
 * The duplicates endpoints eager-load raw `parents` rows, which hold no name,
 * email or phone — those columns live in `public.users` and only the `/parents`
 * endpoints join them in. So the detail view fetches each side by whichever
 * route the signed-in user is allowed to use:
 *
 *   - Teachers and permission holders get `GET /parents/:id`, the same call the
 *     parent registry's detail pane makes: parent, branch and children.
 *   - A plain parent would get a 403 from that, but `GET /parents/by-name` is
 *     only `JwtAuthGuard` and returns the same enriched shape. Since detection
 *     pairs records on matching name + phone, one search for the parent's own
 *     name returns both halves of every pair they are in.
 *
 * Call this with the ids of ONE pair — the selected row. It is deliberately not
 * used to label a whole list: the backend rate-limits JWKS fetches to 10 per
 * minute, so fanning out two calls per row fails most of them. Lists read names
 * from `useParentDirectory` instead, which pages the bulk list endpoint.
 */
export const useDuplicateParties = (ids: string[]) => {
    const canReadAnyParent = useUserTypes().hasAnyType([
        UserTypes.IS_ADMIN_TEACHER,
        UserTypes.IS_GLOBAL_ADMIN
    ])

    // Sorted + de-duplicated so the query list is stable across renders.
    const idKey = ids.filter(Boolean).sort().join(',')
    const uniqueIds = useMemo(
        () => (idKey ? [...new Set(idKey.split(','))] : []),
        [idKey]
    )

    // ─── Teacher / permission-holder path: one lookup per id ───
    const byIdQueries = useQueries({
        queries: (canReadAnyParent ? uniqueIds : []).map((id) => ({
            // Shared with any other consumer of a single parent, so revisiting a
            // row is free.
            queryKey: ['parent', id],
            queryFn: () => fetchParty(id),
            staleTime: PARTY_STALE_MS,
            retry: false
        }))
    })

    // ─── Parent path: own record, then one name search covering the rest ───
    const wantsOwnPath = !canReadAnyParent && uniqueIds.length > 0
    const { parent: me, loading: loadingMe } = useMyParentRecord(wantsOwnPath)
    const myName = getPartyName(me as DuplicateParty | null)

    const byNameQuery = useQuery({
        queryKey: ['parents-by-name', myName],
        queryFn: () => ParentsService.byName(myName),
        enabled: wantsOwnPath && !!myName,
        staleTime: PARTY_STALE_MS,
        retry: false
    })

    // `useQueries` returns a fresh array every render, so the memo below keys off
    // the fetch timestamps instead — they only change when data actually lands.
    const byIdVersion = byIdQueries.map((query) => query.dataUpdatedAt).join(',')

    const parties = useMemo<PartyMap>(() => {
        const map: PartyMap = {}

        if (canReadAnyParent) {
            uniqueIds.forEach((id, index) => {
                const party = byIdQueries[index]?.data
                if (party) map[id] = party
            })
            return map
        }

        // Own record first, so it wins over any staler copy in the search
        // results, then everyone sharing the name — which is both halves of
        // each pair, by construction of the detection query.
        if (me?._id) map[me._id] = me as DuplicateParty
        for (const party of byNameQuery.data ?? []) {
            if (party?._id && !map[party._id]) map[party._id] = party as DuplicateParty
        }
        return map
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canReadAnyParent, uniqueIds, byIdVersion, me, byNameQuery.data])

    const loading = canReadAnyParent
        ? byIdQueries.some((query) => query.isLoading)
        : loadingMe || byNameQuery.isLoading

    // A failed lookup and a genuinely blank record look identical on screen, so
    // the caller is told which happened.
    const error = canReadAnyParent
        ? byIdQueries.find((query) => query.error)?.error ?? null
        : byNameQuery.error ?? null

    return { parties, loading, error: (error as Error | null) ?? null }
}
